import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header";
import "../styles/global.css";
import "../styles/faq.css";
import { Helmet } from "react-helmet";
const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState("general");
  const [expandedIds, setExpandedIds] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Frequently Asked Questions";
    // Initialize AdSense
    try {
      if (window.adsbygoogle && process.env.NODE_ENV !== "development") {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, []);

  const categories = [
    { id: "general", name: "General Questions", icon: "fa-question-circle" },
    {
      id: "assessment",
      name: "About the Assessment",
      icon: "fa-clipboard-check",
    },
    { id: "relationships", name: "Relationship Advice", icon: "fa-heart" },
    { id: "privacy", name: "Privacy & Security", icon: "fa-shield-alt" },
  ];

  const faqs = {
    general: [
      {
        id: "general-1",
        question: "What is Relationship Wellness?",
        answer:
          "Relationship Wellness is an online platform that provides evidence-based tools and resources to help individuals evaluate the health and potential of their romantic relationships. Our cornerstone offering is a relationship assessment that provides personalized insights based on psychological research.",
      },
      {
        id: "general-2",
        question: "Is this service free?",
        answer:
          "Yes, our core relationship assessment is completely free to use. We also offer premium resources and detailed reports for those who want more comprehensive insights, but our basic assessment and recommendations are available at no cost.",
      },
      {
        id: "general-3",
        question: "Do I need to create an account?",
        answer:
          "No, you don't need to create an account to take our relationship assessment. However, creating a free account allows you to save your results, track changes over time, and access additional resources.",
      },
      {
        id: "general-4",
        question: "Can I use this for any type of relationship?",
        answer:
          "Our current assessment is designed specifically for romantic relationships. However, many of the principles and insights can apply to various relationship dynamics. We're working on developing tools for other relationship types in the future.",
      },
      {
        id: "general-5",
        question: "Do you offer relationship counseling?",
        answer:
          "We don't provide direct counseling services, but we can refer you to qualified therapists and counselors in your area who specialize in relationship issues. Our resources section also includes information on finding the right professional help.",
      },
    ],
    assessment: [
      {
        id: "assessment-1",
        question: "How long does the assessment take?",
        answer:
          "The core assessment typically takes about 5-7 minutes to complete. We've designed it to be thorough yet efficient, respecting your time while still gathering enough information to provide meaningful insights.",
      },
      {
        id: "assessment-2",
        question: "How scientific is the assessment?",
        answer:
          "Our assessment is based on established relationship research and psychological principles from peer-reviewed studies. It incorporates factors that have been shown to be significant predictors of relationship success and satisfaction. However, it's designed as a helpful tool, not a clinical diagnosis.",
      },
      {
        id: "assessment-3",
        question: "Can I retake the assessment?",
        answer:
          "Absolutely! You can retake the assessment as many times as you'd like. This can be helpful if your relationship dynamics have changed over time or if you want to reassess after implementing some of our recommendations.",
      },
      {
        id: "assessment-4",
        question: "Should I answer based on how I feel now or overall?",
        answer:
          "We recommend answering based on your general experience over the past few months, rather than how you feel in this specific moment. Relationships naturally have ups and downs, so considering your overall pattern will provide more accurate insights.",
      },
      {
        id: "assessment-5",
        question: "What if my partner and I get different results?",
        answer:
          "It's actually quite common for partners to have different perceptions of their relationship. These differences can be valuable conversation starters. We recommend sharing your results with each other and discussing areas where your experiences differ, ideally with openness and without judgment.",
      },
    ],
    relationships: [
      {
        id: "relationships-1",
        question: "How do I know if I should stay in my relationship?",
        answer:
          "This is a deeply personal decision that depends on many factors. Our assessment can help identify patterns and potential issues, but ultimately, you're the expert on your own experience. Some important considerations include: whether the relationship brings more joy than pain, if both partners are willing to work on issues, whether core values align, and if the relationship supports your growth and wellbeing.",
      },
      {
        id: "relationships-2",
        question: "What are signs of a healthy relationship?",
        answer:
          "Healthy relationships typically include mutual respect, trust, honest communication, support for each other's goals, the ability to resolve conflicts constructively, maintaining individual identities while growing together, and feeling emotionally and physically safe. They also tend to have a good balance of give and take between partners.",
      },
      {
        id: "relationships-3",
        question: "Can a toxic relationship be fixed?",
        answer:
          "While many relationship issues can be improved with awareness, commitment, and sometimes professional help, truly toxic relationships—those involving abuse, control, or persistent disrespect—are very difficult to transform. Both partners must genuinely acknowledge the problems, take responsibility for their part, and commit to substantial change. If abuse is present, safety should be the priority.",
      },
      {
        id: "relationships-4",
        question: "How important is communication in relationships?",
        answer:
          "Communication is fundamental to relationship health. It's how we express needs, resolve conflicts, build intimacy, and coordinate daily life. Research consistently shows that the quality of communication between partners is one of the strongest predictors of relationship satisfaction and longevity.",
      },
      {
        id: "relationships-5",
        question: "Should I take the assessment during a rough patch?",
        answer:
          "It can be helpful to take the assessment during both smooth and challenging periods. If you're going through a rough patch, the assessment might provide perspective on whether your current challenges are part of normal relationship fluctuations or indicative of deeper issues. Just remember that all relationships go through difficult phases, and a temporary rough patch doesn't necessarily mean the relationship isn't viable long-term.",
      },
    ],
    privacy: [
      {
        id: "privacy-1",
        question: "Is my assessment data private?",
        answer:
          "Yes, all assessment responses are confidential. We do not store personally identifiable information with your responses unless you create an account. Your data is never sold to third parties, and we use industry-standard security measures to protect all information.",
      },
      {
        id: "privacy-2",
        question: "Do you share my results with my partner?",
        answer:
          "No, we never share your results with anyone. If you wish to share your insights with your partner, that decision is entirely up to you. You can download or email your results to yourself and then choose to share them however you prefer.",
      },
      {
        id: "privacy-3",
        question: "How do you use my data?",
        answer:
          "We use anonymized data to improve our assessment algorithms and develop better resources. No personally identifiable information is used in this research. If you create an account, we store your results to allow you to access them in the future, but this information is securely protected.",
      },
      {
        id: "privacy-4",
        question: "Can I delete my data?",
        answer:
          "Yes. If you create an account, you can request complete deletion of your data at any time through your account settings. If you take the assessment without an account, there is no personally identifiable information stored that would need to be deleted.",
      },
      {
        id: "privacy-5",
        question: "Is my payment information secure?",
        answer:
          "Absolutely. We never store your full credit card information on our servers. All payment processing is handled by trusted third-party providers (like Stripe or PayPal) that maintain the highest security standards in the industry, including PCI compliance.",
      },
    ],
  };

  const toggleQuestion = (id) => {
    setExpandedIds((prevIds) =>
      prevIds.includes(id)
        ? prevIds.filter((prevId) => prevId !== id)
        : [...prevIds, id]
    );
  };

  const isExpanded = (id) => expandedIds.includes(id);

  return (
    <div className="faq-container">
      <Header />
      <Helmet>
        <title>FAQs</title>
        <meta
          name="description"
          content="FAQ page that contains all frequently asked questions."
        />
      </Helmet>
      <section className="faq-hero">
        <div className="faq-hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            Frequently Asked Questions
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lead-text">
            Find answers to common questions about our relationship assessment
            and resources.
          </motion.p>
        </div>
      </section>

      <section className="faq-main">
        <div className="container">
          <div className="faq-navigation">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-button ${
                  activeCategory === category.id ? "active" : ""
                }`}
                onClick={() => setActiveCategory(category.id)}>
                <i className={`fas ${category.icon}`}></i>
                <span>{category.name}</span>
              </button>
            ))}
          </div>

          {/* Top Ad Placement */}
          <div
            className="ad-container faq-top-ad"
            style={{ width: "100%", minWidth: "300px" }}>
            {/* <div className="ad-label">Advertisement</div> */}
            <ins
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-2067522743161930"
              data-ad-slot="1234567893"
              data-ad-format="auto"
              data-full-width-responsive="true"></ins>
          </div>

          <div className="faq-content">
            <motion.h2
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="category-title">
              <i
                className={`fas ${
                  categories.find((c) => c.id === activeCategory).icon
                }`}></i>
              <span>
                {categories.find((c) => c.id === activeCategory).name}
              </span>
            </motion.h2>

            <div className="faq-list">
              {faqs[activeCategory].map((faq, index) => (
                <motion.div
                  key={faq.id}
                  className="faq-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}>
                  <div
                    className={`faq-question ${
                      isExpanded(faq.id) ? "active" : ""
                    }`}
                    onClick={() => toggleQuestion(faq.id)}>
                    <h3>{faq.question}</h3>
                    <span className="toggle-icon">
                      <i
                        className={`fas fa-chevron-${
                          isExpanded(faq.id) ? "up" : "down"
                        }`}></i>
                    </span>
                  </div>

                  <AnimatePresence>
                    {isExpanded(faq.id) && (
                      <motion.div
                        className="faq-answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}>
                        <p>{faq.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Mid-page Ad after every 3 questions */}
                  {index === 2 && (
                    <div
                      className="ad-container faq-mid-ad"
                      style={{ width: "100%", minWidth: "300px" }}>
                      {/* <div className="ad-label">Advertisement</div> */}
                      <ins
                        className="adsbygoogle"
                        style={{ display: "block" }}
                        data-ad-client="ca-pub-2067522743161930"
                        data-ad-slot="1234567894"
                        data-ad-format="auto"
                        data-full-width-responsive="true"></ins>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Bottom Ad Placement */}
          <div
            className="ad-container faq-bottom-ad"
            style={{ width: "100%", minWidth: "300px" }}>
            {/* <div className="ad-label">Advertisement</div> */}
            <ins
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-2067522743161930"
              data-ad-slot="1234567895"
              data-ad-format="auto"
              data-full-width-responsive="true"></ins>
          </div>
        </div>
      </section>

      <section className="faq-not-found">
        <div className="container">
          <div className="not-found-content">
            <h2>Didn't Find Your Answer?</h2>
            <p>We're here to help. Reach out to our team with your question.</p>
            <a href="/contact" className="contact-button">
              <span>Contact Us</span>
              <i className="fas fa-envelope"></i>
            </a>
          </div>
        </div>
      </section>

      <section className="faq-resources">
        <div className="container">
          <h2 className="section-title">Helpful Resources</h2>

          <div className="resources-grid">
            <a href="/resources/communication" className="resource-card">
              <div className="resource-icon">
                <i className="fas fa-comments"></i>
              </div>
              <div className="resource-content">
                <h3>Communication Techniques</h3>
                <p>
                  Learn effective ways to improve dialogue with your partner
                </p>
              </div>
              <div className="resource-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </a>

            <a href="/resources/conflict-resolution" className="resource-card">
              <div className="resource-icon">
                <i className="fas fa-balance-scale"></i>
              </div>
              <div className="resource-content">
                <h3>Conflict Resolution</h3>
                <p>Strategies for resolving disagreements in a healthy way</p>
              </div>
              <div className="resource-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </a>

            <a href="/blog/signs-of-toxicity" className="resource-card">
              <div className="resource-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div className="resource-content">
                <h3>Warning Signs</h3>
                <p>Recognize patterns of unhealthy relationship dynamics</p>
              </div>
              <div className="resource-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </a>

            <a href="/blog/healthy-boundaries" className="resource-card">
              <div className="resource-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <div className="resource-content">
                <h3>Setting Boundaries</h3>
                <p>How to establish and maintain healthy personal boundaries</p>
              </div>
              <div className="resource-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <h3>Relationship Wellness</h3>
              <p>Evidence-based insights for healthier relationships.</p>
              <div className="social-icons">
                <a href="#" aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href="#" aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="#" aria-label="Twitter">
                  <i className="fab fa-twitter"></i>
                </a>
                <a href="#" aria-label="LinkedIn">
                  <i className="fab fa-linkedin-in"></i>
                </a>
              </div>
            </div>
            <div className="footer-links">
              <h4>Quick Links</h4>
              <ul>
                <li>
                  <a href="/">Home</a>
                </li>
                <li>
                  <a href="/about">About Us</a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>
                <li>
                  <a href="/resources">Resources</a>
                </li>
                <li>
                  <a href="/contact">Contact</a>
                </li>
              </ul>
            </div>
            <div className="footer-contact">
              <h4>Contact Us</h4>
              <p>
                <i className="fas fa-envelope"></i> support@nivexa.net
              </p>
              <p>
                <i className="fas fa-phone"></i> (555) 123-4567
              </p>
              <p>
                <i className="fas fa-map-marker-alt"></i> 123 Wellness Ave,
                Suite 200
                <br />
                San Francisco, CA 94110
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="copyright">
              © {new Date().getFullYear()} Relationship Wellness. All rights
              reserved.
            </p>
            <div className="legal-links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Use</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
